import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Fingerprint, Landmark, LogOut, Smartphone } from 'lucide-react';
import { useAuth } from '../../lib/auth.js';
import { api } from '../../lib/api.js';
import { PushToggle } from '../../lib/PushToggle.js';
import { isNativeApp } from '../../lib/native.js';
import { isStandalone } from './AddToHomePage.js';
import { fmtDate, shiftLabel } from '../../lib/ui.js';
import { appInfo } from '../../lib/native.js';
import { WEB_BUILT_AT } from '../../lib/build.js';
import { Avatar } from '../../lib/components.js';
import { PhotoPicker, photoSrc } from '../../lib/PhotoPicker.js';
import { fmtPhone } from '@siva/shared';
import { t } from '../../i18n/index.js';
import type { BankDetails } from './BankDetailsPage.js';

/**
 * Who the worker is, as the office has them: a table of facts, each with the one thing they
 * can do about it. Web Push is a browser feature; inside the Android app there is no such thing,
 * and the toggle used to sit there saying so. It does not appear there now, and neither does the
 * offer to add the app to the home screen, which the app already is.
 */
export function ProfilePage() {
  const { me, logout, refresh } = useAuth();
  const [bank, setBank] = useState<BankDetails | null>(null);
  const native = isNativeApp();
  /** The app's own build, asked once. Null in a browser, where there is no app to have one. */
  const [app, setApp] = useState<Awaited<ReturnType<typeof appInfo>>>(null);
  useEffect(() => {
    void appInfo().then(setApp);
  }, []);

  // Shown in full: it is the worker's own account number, and a masked one cannot be checked
  // against a passbook, which is the only reason anybody looks at this screen.
  useEffect(() => {
    api<BankDetails>('/me/bank')
      .then(setBank)
      .catch(() =>
        setBank({ bank_name: null, bank_account_number: null, ifsc_code: null, pan_number: null }),
      );
  }, []);

  const user = me?.user;
  if (!user) return null;
  const missing = <span className="muted">{t('bank_missing')}</span>;
  return (
    <div className="stack">
      <section className="card">
        {/* Their own photograph, and the one button that changes it. The owner asked for both
            sides to be able to set it: the worker has the phone and the face, the office keeps
            the ability to fix one that is wrong. */}
        <div className="person-head">
          <Avatar
            name={user.name}
            size={64}
            photoUrl={photoSrc('/me/photo', me?.photo_updated_at)}
          />
          <span>
            {/* No person icon beside the name any more: the avatar to its left is that icon,
                and with a real photograph there it was the same thing drawn twice. */}
            <h2>{user.name}</h2>
            <PhotoPicker
              url="/me/photo"
              hasPhoto={!!me?.photo_updated_at}
              onDone={() => void refresh()}
            />
          </span>
        </div>
        <table className="kv">
          <tbody>
            {user.employee_code && (
              <tr>
                <th scope="row">{t('profile_code')}</th>
                <td>{user.employee_code}</td>
              </tr>
            )}
            <tr>
              <th scope="row">{t('profile_phone')}</th>
              <td>{fmtPhone(user.phone)}</td>
            </tr>
            <tr>
              <th scope="row">{t('profile_type')}</th>
              <td>{user.employment_type ? t(user.employment_type) : '—'}</td>
            </tr>
            {/* The app's own build, for the one question the office cannot answer from here:
                which version is on this phone. The sign-in screen shows it too, but somebody
                already signed in never sees that screen. */}
            {app && (
              <tr>
                <th scope="row">{t('profile_app_version')}</th>
                <td>
                  {t('login_app_version', { name: app.version_name, code: app.version_code })}
                </td>
              </tr>
            )}
            {/* When the page itself was built, which is a different thing from the app's own
                version and answers the question the app version cannot: is the change here. */}
            {WEB_BUILT_AT && (
              <tr>
                <th scope="row">{t('profile_web_build')}</th>
                <td>{fmtDate(WEB_BUILT_AT, true)}</td>
              </tr>
            )}
            <tr>
              <th scope="row">{t('worker_shift')}</th>
              <td>{shiftLabel(user.shift)}</td>
            </tr>
            <tr>
              <th scope="row">{t('profile_join_date')}</th>
              <td>{user.join_date ? fmtDate(user.join_date, false) : '—'}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section className="card">
        <h3 className="with-icon">
          <Fingerprint size={18} aria-hidden="true" /> {t('profile_sign_in')}
        </h3>
        <table className="kv">
          <tbody>
            <tr>
              <th scope="row">{t('profile_passkey')}</th>
              <td>
                {me?.passkey?.registered
                  ? t('passkey_registered_since', { date: fmtDate(me.passkey.since, false) })
                  : t('passkey_not_registered')}
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <Link className="link-block" to="/reregister">
            {t('profile_request_new_passkey')}
          </Link>
        </p>
      </section>

      <section className="card">
        <h3 className="with-icon">
          <Landmark size={18} aria-hidden="true" /> {t('bank_title')}
        </h3>
        <table className="kv">
          <tbody>
            <tr>
              <th scope="row">{t('bank_name')}</th>
              <td>{bank?.bank_name || missing}</td>
            </tr>
            <tr>
              <th scope="row">{t('bank_account')}</th>
              <td>{bank?.bank_account_number || missing}</td>
            </tr>
            <tr>
              <th scope="row">{t('bank_ifsc')}</th>
              <td>{bank?.ifsc_code || missing}</td>
            </tr>
            <tr>
              <th scope="row">{t('bank_pan')}</th>
              <td>{bank?.pan_number || missing}</td>
            </tr>
          </tbody>
        </table>
        <p>
          <Link className="link-block" to="/profile/bank">
            {t('bank_edit')}
          </Link>
        </p>
      </section>

      {/* Only for somebody who has not got the app yet. Inside it, the page this links to
          bounces straight back to the punch screen, so the link would do nothing visible. */}
      {!native && !isStandalone() && (
        <section className="card">
          <h3 className="with-icon">
            <Smartphone size={18} aria-hidden="true" /> {t('app_get_title')}
          </h3>
          <p className="muted">{t('profile_get_app_sub')}</p>
          <p>
            <Link className="link-block" to="/get-the-app">
              {t('profile_get_app_link')}
            </Link>
          </p>
        </section>
      )}

      {!native && (
        <section className="card">
          <PushToggle />
        </section>
      )}

      {/* Reopenable: a worker who skipped the step, or changed phone, can come back to it.
          Hidden once the app is already running from the home screen, and in the app itself. */}
      {!native && !isStandalone() && (
        <section className="card">
          <h3>{t('a2hs_reopen_title')}</h3>
          <p className="muted">{t('a2hs_reopen_help')}</p>
          <Link className="button secondary" to="/add-to-home">
            {t('a2hs_reopen')}
          </Link>
        </section>
      )}

      <section className="card">
        <button type="button" className="secondary" onClick={() => void logout()}>
          <LogOut size={16} aria-hidden="true" /> {t('sign_out')}
        </button>
        <p className="muted">{t('profile_sign_out_help')}</p>
      </section>
    </div>
  );
}
